import type { CreateCustomerContactInput } from '@chukua/common';
import { Form, Input, Modal } from 'antd';
import { useEffect } from 'react';
import type { CustomerContact } from './customers.api';

interface CustomerContactModalProps {
  open: boolean;
  /** The contact being edited, or null when adding a new one. */
  contact: CustomerContact | null;
  submitting: boolean;
  onSubmit: (input: CreateCustomerContactInput) => Promise<void>;
  onCancel: () => void;
}

/** Add a contact, or edit an existing one — same fields either way. */
export function CustomerContactModal({ open, contact, submitting, onSubmit, onCancel }: CustomerContactModalProps) {
  const [form] = Form.useForm<CreateCustomerContactInput>();

  useEffect(() => {
    if (open)
      form.setFieldsValue({
        name: contact?.name ?? '',
        role: contact?.role ?? undefined,
        phone: contact?.phone ?? undefined,
        email: contact?.email ?? undefined,
      });
  }, [open, contact, form]);

  async function submit() {
    await onSubmit(await form.validateFields());
  }

  return (
    <Modal
      open={open}
      title={contact ? 'Edit contact' : 'Add contact'}
      onOk={submit}
      onCancel={onCancel}
      confirmLoading={submitting}
      destroyOnClose
      okText={contact ? 'Save' : 'Add'}
    >
      <Form<CreateCustomerContactInput> form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="role" label="Role">
          <Input />
        </Form.Item>
        <Form.Item name="phone" label="Phone">
          <Input />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
}
